import { useState, type ReactNode } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  type Theme,
} from "@mui/material";
import { ResponsiveTable } from "@/components/Responsive/ResponsiveTable";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { asList, asRecord, hasValue } from "./AllMattersV2Fields";

import { CorporateNumbers } from "./AllMattersV2CorporateNumbers";

export type Tone =
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "default";

const toneColor = (theme: Theme, tone: Tone): string =>
  tone === "default" ? theme.palette.text.secondary : theme.palette[tone].main;
const tint = (theme: Theme, color: string, light = 0.08): string =>
  alpha(color, theme.palette.mode === "dark" ? light * 2 : light);

/** Registry erased text keeps the underline convention of the source PDF. */
export const erasedTextSx = {
  color: "text.secondary",
  textDecoration: "underline",
} as const;

// ---- text formatting ----

const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/;
export const formatDate = (value: unknown): string =>
  typeof value === "string" ? value.replace(isoDate, "$1/$2/$3") : "";

/** Display text for scalars, source-text wrappers, certainty wrappers and lists. */
export const text = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return formatDate(value);
  if (typeof value === "number") return value.toLocaleString("ja-JP");
  if (typeof value === "boolean") return value ? "あり" : "なし";
  if (Array.isArray(value)) return value.map(text).filter(Boolean).join("、");
  const record = asRecord(value);
  if (typeof record.原文 === "string" && record.原文) return record.原文;
  if ("値" in record) return text(record.値);
  return text(record.氏名 ?? record.名称 ?? record.所有者 ?? record.対象者);
};

export const area = (value: unknown): string =>
  typeof value === "number"
    ? `${value.toLocaleString("ja-JP")} ㎡`
    : text(value);

export const yen = (value: unknown): string =>
  typeof value === "number"
    ? `${value.toLocaleString("ja-JP")} 円`
    : text(value);

export const receipt = (value: unknown): string => {
  if (typeof value === "string") return value;
  const record = asRecord(value);
  const parts = [
    formatDate(record.受付日),
    record.受付番号 ? `第${String(record.受付番号)}号` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : text(record.原文);
};

/** Location lines such as `[{所在, 地番: [..]}]` in the current title values. */
export const locations = (value: unknown): string =>
  Array.isArray(value)
    ? value
        .map((entry) => {
          const item = asRecord(entry);
          return typeof entry === "string"
            ? entry
            : [text(item.所在), text(asList(item.地番).join("・"))]
                .filter(Boolean)
                .join(" ");
        })
        .filter(Boolean)
        .join(" / ")
    : text(value);

// ---- badges and chips ----

export const Tag = ({
  label,
  tone = "default",
  filled = false,
  onClick,
  ariaLabel,
}: {
  label: ReactNode;
  tone?: Tone;
  filled?: boolean;
  /** Makes the chip a button, with a phone-sized touch target. */
  onClick?: () => void;
  ariaLabel?: string;
}): JSX.Element => (
  <Chip
    size="small"
    label={label}
    variant="outlined"
    onClick={onClick}
    aria-label={ariaLabel}
    sx={(theme) => {
      const color = toneColor(theme, tone);
      return {
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        color: filled ? theme.palette.getContrastText(color) : color,
        bgcolor: filled ? color : tint(theme, color),
        borderColor: alpha(color, 0.4),
        "& .MuiChip-label": { px: 1 },
        ...(onClick && {
          height: MIN_TAP_TARGET,
          minWidth: MIN_TAP_TARGET,
          fontSize: 13,
          "&:hover, &:focus-visible": { bgcolor: tint(theme, color, 0.16) },
        }),
      };
    }}
  />
);

export const RankBadge = ({
  label,
  appendix = false,
}: {
  label: string;
  appendix?: boolean;
}): JSX.Element => (
  <Box
    component="span"
    sx={(theme) => ({
      display: "inline-block",
      px: 1,
      py: 0.25,
      borderRadius: 1,
      fontSize: 13,
      fontWeight: 700,
      whiteSpace: "nowrap",
      color: appendix ? theme.palette.info.main : theme.palette.text.primary,
      bgcolor: appendix
        ? tint(theme, theme.palette.info.main, 0.12)
        : theme.palette.action.selected,
    })}
  >
    {label}
  </Box>
);

const propertyTones: Record<string, Tone> = {
  土地: "success",
  建物: "warning",
  区分建物: "info",
};
export const PropertyTypeTag = ({ type }: { type: string }): JSX.Element => (
  <Tag label={type} tone={propertyTones[type] ?? "default"} filled />
);

const rightGroups: Array<[Tone, string[]]> = [
  ["primary", ["所有権", "買戻権"]],
  ["warning", ["抵当権", "根抵当権", "質権", "根質権", "先取特権", "抵当証券"]],
  [
    "success",
    ["地上権", "地役権", "賃借権", "永小作権", "採石権", "配偶者居住権"],
  ],
  ["info", ["信託"]],
];
/** Ownership, security, usufruct and trust rights each keep one color. */
export const rightTone = (kind: unknown): Tone =>
  rightGroups.find(([, kinds]) => kinds.includes(String(kind)))?.[0] ??
  "default";

// ---- layout ----

/** A tinted heading band with an accent rule, followed by its content. */
export const Section = ({
  title,
  tone = "primary",
  tags,
  children,
}: {
  title: ReactNode;
  tone?: Tone;
  tags?: ReactNode;
  children?: ReactNode;
}): JSX.Element => (
  <Stack spacing={1} sx={{ minWidth: 0 }}>
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      flexWrap="wrap"
      sx={(theme) => {
        const color =
          tone === "default"
            ? theme.palette.text.secondary
            : theme.palette[tone].main;
        return {
          px: 1.5,
          py: 0.75,
          borderLeft: 4,
          borderColor: color,
          borderRadius: 1,
          bgcolor: tint(theme, color),
        };
      }}
    >
      <Typography component="h3" variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {tags}
    </Stack>
    {children}
  </Stack>
);

/** Label/value rows. Empty values are left out instead of printing dashes. */
export const KeyValues = ({
  items,
}: {
  items: Array<[string, ReactNode]>;
}): JSX.Element | null => {
  const visible = items.filter(
    ([, value]) => value !== null && value !== undefined && value !== ""
  );
  if (!visible.length) return null;
  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "max-content minmax(0, 1fr)",
        columnGap: 2,
        rowGap: 0.75,
        m: 0,
      }}
    >
      {visible.map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Typography
            component="dt"
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {label}
          </Typography>
          <Typography
            component="dd"
            variant="body2"
            sx={{ m: 0, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export interface Column {
  label: string;
  width?: string | number;
}

/** A compact bordered table; cells wrap so the drawer never scrolls sideways. */
export const DataTable = ({
  columns,
  rows,
}: {
  columns: Column[];
  rows: Array<{ key: string | number; cells: ReactNode[]; erased?: boolean }>;
}): JSX.Element => {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <ResponsiveTable
        title="解析結果"
        headers={columns.map((column) => column.label)}
        rows={rows.map((row) =>
          row.cells.map((cell, index) => (
            <Box
              key={index}
              component="span"
              sx={row.erased ? erasedTextSx : undefined}
            >
              {cell === "" || cell == null ? "—" : cell}
            </Box>
          ))
        )}
      />
    );
  }
  return (
    <Box sx={{ overflowX: "auto", minWidth: 0 }}>
      <Table
        size="small"
        sx={{
          border: 1,
          borderColor: "divider",
          "& th, & td": {
            border: 1,
            borderColor: "divider",
            px: 1,
            py: 0.75,
            verticalAlign: "top",
            fontSize: 13,
            overflowWrap: "anywhere",
          },
          "& th": {
            bgcolor: "action.hover",
            fontWeight: 700,
            whiteSpace: "nowrap",
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.label} sx={{ width: column.width }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              sx={row.erased ? { bgcolor: "action.hover" } : undefined}
            >
              {row.cells.map((cell, index) => (
                <TableCell
                  key={index}
                  sx={row.erased ? erasedTextSx : undefined}
                >
                  {cell === "" || cell === null || cell === undefined
                    ? "—"
                    : cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

/** Certainty metadata attached to current values, including party attributes. */
export const CurrentValueNotes = ({
  value,
}: {
  value: unknown;
}): JSX.Element | null => {
  const notes: string[] = [];
  const visit = (item: unknown, label = ""): void => {
    if (Array.isArray(item)) {
      item.forEach((entry) => {
        visit(entry, label);
      });
      return;
    }
    const record = asRecord(item);
    if ("値" in record && typeof record.状態 === "string") {
      const reasons = asList(record.未解決).map(text).filter(Boolean);
      notes.push(
        `${label ? `${label}: ` : ""}${record.状態}${
          reasons.length ? `（${reasons.join("、")}）` : ""
        }`
      );
      visit(record.値, label);
      return;
    }
    Object.entries(record).forEach(([key, entry]) => {
      if (!["問題", "未解決", "問題番号"].includes(key)) visit(entry, key);
    });
  };
  visit(value);
  return notes.length ? (
    <Stack spacing={0.25}>
      {notes.map((note, index) => (
        <Typography key={index} variant="caption" component="div">
          {note}
        </Typography>
      ))}
    </Stack>
  ) : null;
};

/** Name with its address below it, so party tables stay narrow on phones. */
export const PartyName = ({ party }: { party: unknown }): JSX.Element => {
  const item = asRecord(party);
  const name = typeof party === "string" ? party : text(item);
  const address = text(item.住所 ?? item.所有者住所 ?? item.本店);
  const branch = text(item.取扱店);
  return (
    <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
      <Typography variant="body2" fontWeight={600} component="div">
        {name || "—"}
      </Typography>
      {address && (
        <Typography variant="caption" color="text.secondary" component="div">
          {address}
        </Typography>
      )}
      {hasValue(item.会社法人等番号) && (
        <Typography variant="caption" color="text.secondary" component="div">
          会社法人等番号（登記記載）: {text(item.会社法人等番号)}
        </Typography>
      )}
      <CorporateNumbers party={item} />
      {hasValue(item.国内連絡先) && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            国内連絡先
          </Typography>
          <PartyName party={item.国内連絡先} />
        </Box>
      )}
      <CurrentValueNotes value={party} />
      {branch && (
        <Typography variant="caption" color="text.secondary" component="div">
          取扱店 {branch}
        </Typography>
      )}
    </Box>
  );
};

/**
 * 役割 / 氏名・住所 / 持分. The share column appears only when used.
 * `extras` are shown under each party's name, in the same order.
 */
export const PartyTable = ({
  parties,
  defaultRole = "",
  extras,
}: {
  parties: unknown[];
  defaultRole?: string;
  extras?: ReactNode[];
}): JSX.Element | null => {
  if (!parties.length) return null;
  const withShare = parties.some((party) => hasValue(asRecord(party).持分));
  const role = (party: unknown): string =>
    text(asRecord(party).ラベル ?? asRecord(party).役割) || defaultRole;
  const withRole = parties.some((party) => role(party));
  return (
    <DataTable
      columns={[
        ...(withRole ? [{ label: "役割", width: "6.5em" }] : []),
        { label: "氏名・住所" },
        ...(withShare ? [{ label: "持分", width: "7.5em" }] : []),
      ]}
      rows={parties.map((party, index) => {
        const item = asRecord(party);
        return {
          key: index,
          cells: [
            ...(withRole ? [role(party)] : []),
            extras?.[index] ? (
              <Stack key="name" spacing={0.5}>
                <PartyName party={party} />
                {extras[index]}
              </Stack>
            ) : (
              <PartyName key="name" party={party} />
            ),
            ...(withShare ? [text(item.持分)] : []),
          ],
        };
      })}
    />
  );
};

export const PageLink = ({
  page,
  onShowPage,
}: {
  page: unknown;
  onShowPage: (page: number) => void;
}): JSX.Element | null =>
  typeof page === "number" && Number.isInteger(page) && page > 0 ? (
    <Button
      size="small"
      sx={{ minHeight: 44, alignSelf: "flex-start" }}
      onClick={() => {
        onShowPage(page);
      }}
    >
      PDF {page}ページ
    </Button>
  ) : null;

/**
 * Source lines from the PDF. Long bodies start folded; erased lines keep the
 * registry underline and can be hidden with the section switch.
 */
export const SourceLines = ({
  lines,
  erasures = [],
  showErased,
  label,
  collapsedLines,
}: {
  lines: unknown[];
  erasures?: unknown[];
  showErased: boolean;
  label: string;
  collapsedLines: number;
}): JSX.Element | null => {
  const [open, setOpen] = useState(false);
  const visible = lines
    .map((line, index) => ({ line: String(line), erased: erasures[index] }))
    .filter((line) => showErased || line.erased !== true);
  if (!visible.length) return null;
  const shown = open ? visible : visible.slice(0, collapsedLines);
  const foldable = visible.length > collapsedLines;
  return (
    <Box>
      {shown.length > 0 && (
        <Box sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: "action.hover" }}>
          {shown.map((line, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                ...(line.erased === true ? erasedTextSx : {}),
              }}
            >
              {line.line}
            </Typography>
          ))}
        </Box>
      )}
      {foldable && (
        <Button
          size="small"
          color="inherit"
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
          }}
          startIcon={
            <ExpandMoreIcon
              sx={{ transform: open ? "rotate(180deg)" : "none" }}
            />
          }
          sx={{ minHeight: 44, color: "text.secondary" }}
        >
          {open
            ? `${label}を閉じる`
            : collapsedLines === 0
            ? `${label}を表示（${visible.length}行）`
            : `${label}の続きを表示（残り${
                visible.length - collapsedLines
              }行）`}
        </Button>
      )}
    </Box>
  );
};
